// Full-screen sign-in gate. Rendered by App.tsx when there's no
// signed-in user. Initializes GIS, renders the official Google
// button, and on success calls setUser.
//
// Adapted from agicore-foundry's SignInGate — same GIS lifecycle and
// the same dangerouslySetInnerHTML guard against React/GIS unmount
// crashes — restyled into BKA's warm-publication register and with
// foundry's usage-ping dependency dropped (not in Sprint 0 scope).
//
// When VITE_GOOGLE_CLIENT_ID is unset, shows a clear "operator setup
// needed" message instead of a broken sign-in surface.

import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../store/authStore";
import {
  initializeGoogleAuth, renderSignInButton, googleAuthClientId,
} from "../lib/google-auth";

export function SignInGate() {
  const setUser = useAuthStore((s) => s.setUser);
  const setReady = useAuthStore((s) => s.setReady);
  const ready = useAuthStore((s) => s.ready);
  const [error, setError] = useState<string | null>(null);
  const btnRef = useRef<HTMLDivElement>(null);

  const clientId = googleAuthClientId();

  useEffect(() => {
    if (!clientId) {
      setError("VITE_GOOGLE_CLIENT_ID is not configured. Add it to .env.local (dev) or the Cloudflare Pages environment (prod).");
      return;
    }
    let cancelled = false;
    const container = btnRef.current;
    initializeGoogleAuth({
      clientId,
      onSignIn: (user) => setUser(user),
    }).then(async () => {
      if (cancelled) return;
      setReady(true);
      if (container) {
        try {
          await renderSignInButton(container);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    }).catch((e) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : String(e));
    });
    return () => {
      cancelled = true;
      // Belt + suspenders: clear GIS-mutated children before React
      // tears down the gate so the unmount doesn't trip over them.
      if (container) {
        while (container.firstChild) container.removeChild(container.firstChild);
      }
    };
  }, [clientId, setUser, setReady]);

  return (
    <div className="gate-root">
      <div className="gate-card">
        <div className="gate-mark">BKA</div>
        <div className="gate-tagline">you are the platform</div>
        <div className="gate-explain">
          Sign in with Google to open your studio.
          <br />
          Your content lives in <em>your</em> GitHub repos and <em>your</em> Cloudflare account —
          identity is just so the app knows whose studio to open. We host nothing.
        </div>
        {clientId && !ready && <div className="gate-loading">loading google sign-in…</div>}
        {clientId && (
          // dangerouslySetInnerHTML tells React not to reconcile this
          // node's children. GIS mutates the container with its own
          // button + iframes; without this, React crashes on unmount
          // (after sign-in success) trying to removeChild nodes it
          // never tracked.
          <div
            className="gate-button"
            ref={btnRef}
            dangerouslySetInnerHTML={{ __html: "" }}
          />
        )}
        {error && (
          <div className="gate-error">
            <div className="gate-error-title">can't sign in</div>
            <div className="gate-error-body">{error}</div>
          </div>
        )}
      </div>
      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .gate-root {
        position: fixed; inset: 0;
        background:
          radial-gradient(circle at 28% 18%, rgba(13,110,110,0.07), transparent 42%),
          radial-gradient(circle at 75% 82%, rgba(180,80,42,0.06), transparent 45%),
          var(--paper);
        display: flex; align-items: center; justify-content: center;
        z-index: 2000;
      }
      .gate-card {
        width: min(440px, 92vw);
        padding: var(--space-7) var(--space-6);
        background: var(--paper-raised);
        border: 1px solid var(--line);
        border-radius: var(--radius-2);
        box-shadow: var(--shadow-2);
        display: flex; flex-direction: column;
        align-items: center;
        gap: var(--space-3);
      }
      .gate-mark {
        font-family: var(--font-serif);
        font-size: 40px;
        font-weight: 600;
        letter-spacing: 0.04em;
        color: var(--teal);
      }
      .gate-tagline {
        font-family: var(--font-sans);
        font-size: 12px;
        color: var(--ink-soft);
        text-transform: uppercase;
        letter-spacing: 0.22em;
      }
      .gate-explain {
        text-align: center;
        font-family: var(--font-serif);
        font-size: 15px;
        line-height: 1.65;
        color: var(--ink-soft);
        margin: var(--space-4) 0 var(--space-2);
        max-width: 340px;
      }
      .gate-explain em { color: var(--ink); font-style: italic; }
      .gate-button {
        min-height: 44px;
        display: flex; align-items: center; justify-content: center;
      }
      .gate-loading {
        font-family: var(--font-mono);
        font-size: 11px;
        color: var(--ink-faint);
        font-style: italic;
      }
      .gate-error {
        margin-top: var(--space-3);
        padding: var(--space-2) var(--space-3);
        background: var(--rust-bg);
        border: 1px solid rgba(180,80,42,0.30);
        border-radius: var(--radius-1);
        color: var(--rust);
        font-size: 12px;
        max-width: 360px;
      }
      .gate-error-title {
        font-family: var(--font-mono);
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        margin-bottom: 4px;
      }
      .gate-error-body { color: var(--ink-soft); line-height: 1.5; }
    `}</style>
  );
}
